import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Boxes, PackageX, ShieldAlert, Truck, Undo2 } from 'lucide-react';
import { SalesReturnStatus, type SalesReturnSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { useBranch } from '@/hooks/useBranch';
import { apiErrorMessage } from '@/services/apiClient';
import { useCustodyReportQuery, useReturnsQuery } from '../api/returns.api';

const PAGE_SIZE = 20;

const STATUS_TONES: Record<SalesReturnStatus, 'brand' | 'neutral'> = {
  OPEN: 'brand',
  CANCELLED: 'neutral',
};

/**
 * Returns, and what the branch is physically holding.
 *
 * The custody strip sits above the list because it answers the question the list cannot: sellable
 * stock is not the same as stock in the building. Goods at the lab and goods in quarantine are
 * both on the premises and neither is on the shelf, so a count that only checks sellable stock
 * reports a shortage for things sitting in plain sight.
 */
export function ReturnsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeBranch } = useBranch();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SalesReturnStatus | ''>('');
  const [quarantinedOnly, setQuarantinedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const custody = useCustodyReportQuery(Boolean(activeBranch));
  const { data, isPending, isError, error, refetch, isFetching } = useReturnsQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    status,
    quarantinedOnly,
  });

  const columns: Column<SalesReturnSummary>[] = [
    {
      key: 'reference',
      header: t('sales.fields.reference'),
      primary: true,
      render: (row) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {row.reference}
          </span>
          <span className="text-muted text-xs">
            {row.customerName ?? t('sales.invoice.walkIn')} · {row.branchName}
          </span>
        </span>
      ),
    },
    {
      key: 'order',
      header: t('sales.returns.fields.order'),
      render: (row) => (
        <span className="text-muted font-mono text-xs" dir="ltr">
          {row.orderReference}
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('sales.returns.fields.quantity'),
      align: 'end',
      render: (row) => (
        <span className="text-ink font-medium" dir="ltr">
          {row.totalQuantity}
        </span>
      ),
    },
    {
      key: 'quarantined',
      header: t('sales.returns.fields.quarantined'),
      align: 'end',
      render: (row) =>
        row.quarantinedQuantity > 0 ? (
          <Badge tone="warning">{String(row.quarantinedQuantity)}</Badge>
        ) : (
          <span className="text-muted text-xs">{t('sales.returns.allInspected')}</span>
        ),
    },
    {
      key: 'status',
      header: t('sales.fields.status'),
      render: (row) => (
        <Badge tone={STATUS_TONES[row.status] ?? 'neutral'}>
          {t(`sales.returns.statuses.${row.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('sales.returns.openReturn', { reference: row.reference })}
          onClick={() => navigate(`/sales/returns/${row.id}`)}
        >
          {t('sales.returns.open')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('sales.returns.title')} subtitle={t('sales.returns.subtitle')} />

      {!activeBranch ? <Alert tone="warning">{t('sales.returns.selectBranch')}</Alert> : null}

      {activeBranch ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={Boxes}
            label={t('sales.returns.custody.sellable')}
            value={custody.data?.sellableQuantity ?? 0}
            hint={t('sales.returns.custody.sellableHint')}
            isLoading={custody.isPending}
          />
          <StatCard
            icon={Truck}
            label={t('sales.returns.custody.issued')}
            value={custody.data?.issuedNotDeliveredQuantity ?? 0}
            hint={t('sales.returns.custody.issuedHint')}
            tone="warning"
            isLoading={custody.isPending}
          />
          <StatCard
            icon={ShieldAlert}
            label={t('sales.returns.custody.quarantined')}
            value={custody.data?.quarantinedQuantity ?? 0}
            hint={t('sales.returns.custody.quarantinedHint')}
            tone="danger"
            isLoading={custody.isPending}
          />
          <StatCard
            icon={PackageX}
            label={t('sales.returns.custody.total')}
            value={custody.data?.totalUnderCustodyQuantity ?? 0}
            hint={t('sales.returns.custody.totalHint')}
            isLoading={custody.isPending}
          />
        </div>
      ) : null}

      <Card>
        <CardHeader
          title={t('sales.returns.listTitle')}
          subtitle={
            data ? t('sales.returns.count', { count: data.pagination.totalItems }) : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-52">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t('sales.returns.searchPlaceholder')}
                  aria-label={t('sales.returns.searchLabel')}
                />
              </div>
              <div className="w-36">
                <Select
                  options={[
                    { value: '', label: t('sales.returns.allStatuses') },
                    ...Object.values(SalesReturnStatus).map((value) => ({
                      value,
                      label: t(`sales.returns.statuses.${value}`),
                    })),
                  ]}
                  value={status}
                  aria-label={t('sales.fields.status')}
                  onChange={(event) => {
                    setStatus(event.target.value as SalesReturnStatus | '');
                    setPage(1);
                  }}
                />
              </div>
              <Button
                variant={quarantinedOnly ? 'primary' : 'secondary'}
                size="sm"
                aria-pressed={quarantinedOnly}
                onClick={() => {
                  setQuarantinedOnly((current) => !current);
                  setPage(1);
                }}
              >
                <ShieldAlert className="size-4" aria-hidden />
                {t('sales.returns.quarantinedFilter')}
              </Button>
            </div>
          }
        />

        <DataTable
          caption={t('sales.returns.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(row) => row.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('sales.returns.errors.load')}
          onRetry={() => void refetch()}
          emptyIcon={Undo2}
          emptyTitle={t('sales.returns.empty')}
          emptyDescription={t('sales.returns.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
